import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
    AsyncValidatorFn,
    FormControl,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup
} from '@angular/forms';

import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import {
    DropdownDirection,
    FormValue,
    FormValueRequired,
    FormValueState,
    MakeProvider,
    MultiFormSelectOptions,
    MultiValueFormInput,
    TypedValue,
    typedValueComparator,
    UserService
} from '@stylehaven/components';
import { unionWith } from 'lodash-es';
import isEqual from 'lodash-es/isEqual';
import { GroupsToFormSelectOptions } from '../helpers/groups-to-formselectoptions.helper';

export type AbstractControlStatus = 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED';

@Component({
    standalone: false,
    selector: 'med-form-permissions-complex-base',
    templateUrl: './form-permissions-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(FormPermissionsComplexBaseComponent)]
})
export class FormPermissionsComplexBaseComponent extends MultiValueFormInput<TypedValue> implements OnInit {
    @Input() id: string;
    @Input() readLabel: string;
    @Input() writeLabel: string;
    @Input() exportLabel: string;
    @Input() helpText: string;

    @Input() errorMsg: string;
    @Input() noneSelectedMsg: string;
    @Input() multipleSelectedMsg: string;
    @Input() allSelectedMsg: string;

    @Input() openDirection: DropdownDirection = DropdownDirection.Below;

    @Input() canBeIndeterminate: false;
    @Input() indeterminateMsg: '';
    @Input() isBatchEdit = false;
    @Input() isDisabled = false;

    private permissionsForm: UntypedFormGroup;

    public permissionOptions$: Observable<MultiFormSelectOptions>;

    public multiple = true;
    private emitValues$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private savedInput: FormValue<TypedValue>[] = [];

    constructor(
        private readonly userService: UserService,
        private readonly formBuilder: UntypedFormBuilder,
        cd: ChangeDetectorRef
    ) {
        super(cd);
    }

    public ngOnInit(): void {
        this.initForm();
        this.initPermissionOptions();
        this.initOnChangeEvents();
    }

    private initForm(): void {
        this.permissionsForm = new FormGroup({
            readPermissions: new FormControl(undefined, {
                validators: this.isBatchEdit ? null : FormValueRequired
            }),
            writePermissions: new FormControl(undefined, {
                validators: this.isBatchEdit ? null : FormValueRequired,
                asyncValidators: this.isBatchEdit ? null : this.getCustomRequiredValidator()
            }),
            exportPermissions: new FormControl()
        });

        if (this.isDisabled) {
            this.readControl.disable();
            this.writeControl.disable();
            this.exportControl.disable();
        }
    }

    private getCustomRequiredValidator(): AsyncValidatorFn {
        return control =>
            !this.permissionOptions$
                ? of(null)
                : this.permissionOptions$.pipe(
                      map(options => options.options.map(option => option.value)),
                      map(options =>
                          control.value.some(
                              (formValue: FormValue<string>) =>
                                  options.includes(formValue.value) && formValue.state === FormValueState.SET
                          )
                      ),
                      map(hasSelectableValue =>
                          hasSelectableValue
                              ? null
                              : {
                                    required: true
                                }
                      ),
                      take(1)
                  );
    }

    private initPermissionOptions(): void {
        this.permissionOptions$ = this.userService.getAssignableUserGroups().pipe(
            map(groups => GroupsToFormSelectOptions(groups)),
            tap(() => this.emitValues$.next(true)),
            takeUntil(this.destroy$),
            shareReplay(1)
        );
    }

    private initOnChangeEvents(): void {
        const readValueChanges$: Observable<FormValue<string>[]> = this.readControl.valueChanges;
        const writeValueChanges$: Observable<FormValue<string>[]> = this.writeControl.valueChanges;
        const exportValueChanges$: Observable<FormValue<string>[]> = this.exportControl.valueChanges;

        readValueChanges$
            .pipe(
                distinctUntilChanged((a, b) => isEqual(a, b)),
                withLatestFrom(this.emitValues$),
                filter(([_, emit]) => emit === true),
                map(([listValues, _]) => listValues),
                takeUntil(this.destroy$)
            )
            .subscribe(values => this.onReadChange(values));

        merge(writeValueChanges$, exportValueChanges$)
            .pipe(
                distinctUntilChanged((a, b) => isEqual(a, b)),
                withLatestFrom(this.emitValues$),
                filter(([_, emit]) => emit === true),
                map(([listValues, _]) => listValues),
                takeUntil(this.destroy$)
            )
            .subscribe(values => this.onWriteExportChange(values));

        combineLatest([readValueChanges$, writeValueChanges$, exportValueChanges$])
            .pipe(
                distinctUntilChanged((a, b) => isEqual(a, b)),
                withLatestFrom(this.emitValues$),
                filter(([_, emit]) => emit === true),
                map(([permissionValues, _]) => permissionValues),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.emitState(
                    this.getControlValue(this.readControl),
                    this.getControlValue(this.writeControl),
                    this.getControlValue(this.exportControl)
                );
            });
    }

    private emitState(
        readValues: readonly FormValue<string>[],
        writeValues: readonly FormValue<string>[],
        exportValues: readonly FormValue<string>[]
    ): void {
        const readTypedValues: FormValue<TypedValue>[] = readValues.map(
            res => new FormValue({ type: 'Read', value: res.value }, res.state)
        );
        const writeTypedValues: FormValue<TypedValue>[] = writeValues.map(
            res => new FormValue({ type: 'Write', value: res.value }, res.state)
        );
        const exportTypedValues: FormValue<TypedValue>[] = exportValues.map(
            res => new FormValue({ type: 'Export', value: res.value }, res.state)
        );
        const converted: FormValue<TypedValue>[] = [...readTypedValues, ...writeTypedValues, ...exportTypedValues];
        const merged: FormValue<TypedValue>[] = this.mergeWithSaved(converted, this.savedInput);
        this.propagateChange(merged);
    }

    private mergeWithSaved(
        formValues: readonly FormValue<TypedValue>[],
        savedValues: readonly FormValue<TypedValue>[]
    ): FormValue<TypedValue>[] {
        return unionWith(formValues, savedValues, (a, b) => typedValueComparator(a.value, b.value));
    }

    writeValueImpl(input: readonly FormValue<TypedValue>[]): void {
        this.savedInput = [...input];
        this.emitValues$.next(false);

        const newReadTypedValues: FormValue<TypedValue>[] = input.filter(inp => inp.value.type === 'Read');
        const newWriteTypedValues: FormValue<TypedValue>[] = input.filter(inp => inp.value.type === 'Write');
        const newExportTypedValues: FormValue<TypedValue>[] = input.filter(inp => inp.value.type === 'Export');

        const newReadValues: FormValue<string>[] = newReadTypedValues.map(res => this.formTypedValueToFormValue(res));
        const newWriteValues: FormValue<string>[] = newWriteTypedValues.map(res => this.formTypedValueToFormValue(res));
        const newExportValues: FormValue<string>[] = newExportTypedValues.map(res =>
            this.formTypedValueToFormValue(res)
        );

        this.setControlValues(this.readControl, newReadValues);
        this.setControlValues(this.writeControl, newWriteValues);
        this.setControlValues(this.exportControl, newExportValues);

        this.emitValues$.next(true);

        this.emitState(
            this.getControlValue(this.readControl),
            this.getControlValue(this.writeControl),
            this.getControlValue(this.exportControl)
        );
    }

    private formTypedValueToFormValue(typedFormValue: FormValue<TypedValue>): FormValue<string> {
        return new FormValue(typedFormValue.value.value, typedFormValue.state);
    }

    private onReadChange(values: FormValue<string>[]): void {
        this.emitValues$.next(false);
        this.uncheckWriteExportOptions(values);
        this.emitValues$.next(true);
    }

    private onWriteExportChange(values: FormValue<string>[]): void {
        this.emitValues$.next(false);
        this.checkReadOptions(values);
        this.emitValues$.next(true);
    }

    private uncheckWriteExportOptions(readValues: FormValue<string>[]): void {
        this.uncheckMissingReadValues(this.writeControl, readValues);
        this.uncheckMissingReadValues(this.exportControl, readValues);
    }

    private uncheckMissingReadValues(control: UntypedFormControl, readValues: FormValue<string>[]): void {
        // MED-6658: prevent automatic uncheck interactivity in Batch edit
        if (this.isBatchEdit) {
            return;
        }

        const controlValues: FormValue<string>[] = this.getControlValue(control);
        const unsetReadKeys: string[] = readValues
            .filter(readValue => readValue.state === FormValueState.UNSET)
            .map(readValue => readValue.value);

        if (controlValues && unsetReadKeys) {
            const newState: FormValue<string>[] = controlValues.map(controlValue => {
                if (unsetReadKeys.includes(controlValue.value)) {
                    return new FormValue(controlValue.value, FormValueState.UNSET);
                }
                return controlValue;
            });

            this.setControlValues(control, newState);
        }
    }

    private checkReadOptions(values: FormValue<string>[]): void {
        // MED-6658: prevent automatic uncheck interactivity in Batch edit
        if (this.isBatchEdit) {
            return;
        }

        const readControlValues: FormValue<string>[] = this.getControlValue(this.readControl);

        if (values) {
            const keysToCheck: string[] = values
                .filter(readValue => readValue.state === FormValueState.SET)
                .map((formValue: FormValue<string>) => formValue.value);

            const readValueKeys: string[] = readControlValues.map((formValue: FormValue<string>) => formValue.value);

            const existingKeys: string[] = keysToCheck.filter(key => readValueKeys.includes(key));
            const missingKeys: string[] = keysToCheck.filter(key => !readValueKeys.includes(key));

            const setExisting: FormValue<string>[] = readControlValues.map(readControlValue => {
                if (existingKeys.includes(readControlValue.value)) {
                    return new FormValue(readControlValue.value, FormValueState.SET);
                }
                return readControlValue;
            });

            const setMissing: FormValue<string>[] = missingKeys.map(missingKey => {
                return new FormValue(missingKey, FormValueState.SET);
            });

            const newState: FormValue<string>[] = [...setExisting, ...setMissing];
            this.setControlValues(this.readControl, newState);
        }
    }

    // For typing
    private setControlValues(control: UntypedFormControl, values: FormValue<string>[]): void {
        control.setValue(values);
    }

    // For typing
    private getControlValue(control: UntypedFormControl): FormValue<string>[] {
        return control.value;
    }

    get readControl(): UntypedFormControl {
        return this.permissionsForm.get('readPermissions') as UntypedFormControl;
    }

    get writeControl(): UntypedFormControl {
        return this.permissionsForm.get('writePermissions') as UntypedFormControl;
    }

    get exportControl(): UntypedFormControl {
        return this.permissionsForm.get('exportPermissions') as UntypedFormControl;
    }
}
